import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signUp, confirmSignUp, signOut, resendSignUpCode } from 'aws-amplify/auth';
import { useAlert } from '../components/Alert';
import { authAPI } from '../services/api';
import { logEvent } from '../utils/analytics';
import PublicNavigation from '../components/PublicNavigation';
import TermsModal from '../components/TermsModal';
import Maintenance from './Maintenance';
import './Auth.css';

const PENDING_VERIFICATION_KEY = 'pendingVerificationEmail';

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const [step, setStep] = useState('signup'); // 'signup' or 'confirm'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState('');
  const [termsViewed, setTermsViewed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Check if maintenance mode is enabled (after all hooks are called)
  const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    // Check if user was explicitly redirected here (e.g., from login)
    const pendingEmailFromState = location.state?.pendingVerificationEmail;
    
    if (pendingEmailFromState) {
      // User was redirected - show confirmation
      setStep('confirm');
      setFormData((prev) => ({
        ...prev,
        email: pendingEmailFromState,
      }));
      localStorage.setItem(PENDING_VERIFICATION_KEY, pendingEmailFromState);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      // User navigated directly to /signup - clear any pending email from storage
      const pendingEmailFromStorage = localStorage.getItem(PENDING_VERIFICATION_KEY);
      if (pendingEmailFromStorage && location.pathname === '/signup') {
        // Clear stale pending email since user is starting fresh
        localStorage.removeItem(PENDING_VERIFICATION_KEY);
      }
    }

    // Check if user previously viewed terms (from session storage)
    const viewedTerms = sessionStorage.getItem('termsViewed');
    if (viewedTerms === 'true') {
      setTermsViewed(true);
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [resendCooldown]);

  // Check maintenance mode after all hooks
  if (maintenanceMode) {
    return <Maintenance />;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { name, email, password } = formData;

    try {

      if (!name || !email || !password) {
        setError('Name, email, and password are required');
        setLoading(false);
        return;
      }

      if (!termsViewed) {
        setError('Please read the Terms & Conditions before signing up');
        setLoading(false);
        return;
      }

      if (!termsAccepted) {
        setError('You must accept the Terms & Conditions to create an account');
        setLoading(false);
        return;
      }

      // Sign out any existing session first
      try {
        await signOut();
      } catch (err) {
        // Ignore if not signed in
      }

      // Store name in localStorage for backend registration
      // These will be retrieved during first login and saved to DynamoDB
      // Only email is stored in Cognito
      localStorage.setItem('pendingUserData', JSON.stringify({
        name: name,
      }));

      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });

      // Track signup attempt
      logEvent('signup', {
        category: 'user',
        method: 'email'
      });

      setStep('confirm');
      localStorage.setItem(PENDING_VERIFICATION_KEY, email);
    } catch (err) {
      if (err.name === 'UsernameExistsException') {
        try {
          await resendSignUpCode({ username: email });
          setStep('confirm');
          setResendStatus('Account already exists but is not verified. We sent a new verification email.');
          localStorage.setItem(PENDING_VERIFICATION_KEY, email);
        } catch (resendError) {
          const message = resendError?.message || 'Account exists but verification email could not be resent. Please try again later.';
          if (message.includes('Auto verification not turned on')) {
            setError('Verification email could not be resent because email verification is disabled. Please contact support.');
          } else {
            setError(message);
          }
        }
      } else {
        console.error('Sign up error:', err);
        setError(err.message || 'Failed to sign up');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode,
      });

      // After confirmation, user needs to sign in
      // Then register in our backend
      showAlert('Account confirmed! Please log in.', 'success');
      localStorage.removeItem(PENDING_VERIFICATION_KEY);
      navigate('/login');
    } catch (err) {
      console.error('Confirmation error:', err);
      setError(err.message || 'Failed to confirm account');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!formData.email || resendCooldown > 0) {
      return;
    }

    try {
      setResendStatus('');
      await resendSignUpCode({ username: formData.email });
      setResendStatus('Verification email resent. Please check your inbox (and spam folder).');
      setResendCooldown(60);
      localStorage.setItem(PENDING_VERIFICATION_KEY, formData.email);
    } catch (err) {
      const message = err?.message || 'Failed to resend verification email.';
      if (message.includes('Auto verification not turned on')) {
        setResendStatus('Unable to resend verification email because email verification is disabled. Please contact support.');
      } else {
        setResendStatus(message);
      }
    }
  };

  const handleViewTerms = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const handleTermsViewed = () => {
    setTermsViewed(true);
    sessionStorage.setItem('termsViewed', 'true');
  };

  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
  };

  if (step === 'confirm') {
    return (
      <div className="auth-page">
        <PublicNavigation />
        <div className="auth-container">
          <div className="auth-card">
            <h1 className="auth-title">Confirm Your Account</h1>
            <p className="auth-subtitle">
              We sent a confirmation code to {formData.email}
            </p>

            {error && <div className="error-message">{error}</div>}
          {resendStatus && (
            <div className="info-message" style={{ marginBottom: '16px' }}>
              {resendStatus}
            </div>
          )}

            <form onSubmit={handleConfirm} className="auth-form">
              <div className="form-group">
                <label className="form-label">Confirmation Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>
            </form>

            <div className="auth-footer confirm-footer">
              <button
                type="button"
                className="link-button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0
                  ? `Resend available in ${resendCooldown}s`
                  : 'Resend verification email'}
              </button>
              <button
                onClick={() => {
                  setStep('signup');
                  // Clear pending email when going back to signup
                  localStorage.removeItem(PENDING_VERIFICATION_KEY);
                  setFormData(prev => ({ ...prev, email: '' }));
                }}
                className="link-button"
              >
                Start Fresh / Back to Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <PublicNavigation />
      <TermsModal 
        isOpen={showTermsModal}
        onClose={handleCloseTermsModal}
        onTermsViewed={handleTermsViewed}
      />
      <div className="auth-container">
        <div className="auth-card">
          <h1 className="auth-title">Create Your Account</h1>
          <p className="auth-subtitle">Join leagues and compete with players</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSignUp} className="auth-form">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 8 characters"
                required
                minLength={8}
              />
            </div>


            <div className="form-group">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={!termsViewed}
                    style={{ marginTop: '4px', width: '18px', height: '18px', cursor: termsViewed ? 'pointer' : 'not-allowed' }}
                  />
                  <label 
                    htmlFor="terms-checkbox" 
                    style={{ 
                      fontSize: '0.9rem', 
                      cursor: termsViewed ? 'pointer' : 'not-allowed',
                      opacity: termsViewed ? 1 : 0.6
                    }}
                  >
                    I have read and accept the{' '}
                    <button 
                      type="button"
                      onClick={handleViewTerms}
                      style={{ 
                        color: '#007bff', 
                        textDecoration: 'underline', 
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit'
                      }}
                    >
                      Terms & Conditions
                    </button>
                  </label>
                </div>
                {!termsViewed && (
                  <small style={{ color: '#666', marginLeft: '26px', fontSize: '0.85rem' }}>
                    Please click the link above to read the Terms & Conditions first
                  </small>
                )}
              </div>
            </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading || !termsAccepted}>
                {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;

